import React, { useState, Fragment } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { ComplianceAssistant } from '../components/ComplianceAssistant';
import { ComplianceNews } from '../components/ComplianceNews';
import { Search, Filter, FileText, Download, AlertTriangle, CheckCircle, Clock, Eye, ArrowRight, BarChart3, Calendar, PieChart, Bell, ChevronDown, ArrowUpRight, Beaker, ClipboardList, MessageSquare, MoreVertical } from 'lucide-react';
export function Compliance() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAuthority, setFilterAuthority] = useState('all');
  const [activeTab, setActiveTab] = useState('issues');
  const [expandedIssue, setExpandedIssue] = useState<number | null>(null);
  const complianceIssues = [{
    id: 1,
    product: 'Organic Granola Bars - Chocolate Chip',
    issue: 'Contains Red Food Coloring #3 (FDA phase-out by 2026)',
    authority: 'FDA',
    status: 'critical',
    dueDate: 'Jan 2026',
    image: 'https://images.unsplash.com/photo-1560963689-b5682b6440f8?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 2,
    product: 'Protein Shake Mix - Vanilla',
    issue: 'Contains Red Food Coloring #3 (FDA phase-out by 2026)',
    authority: 'FDA',
    status: 'critical',
    dueDate: 'Jan 2026',
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 3,
    product: 'Organic Quinoa Salad',
    issue: 'May not meet new USDA organic certification requirements',
    authority: 'USDA',
    status: 'warning',
    dueDate: 'Mar 2025',
    image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 4,
    product: 'Almond Butter - Smooth',
    issue: 'Allergen labeling needs update per new FDA guidelines',
    authority: 'FDA',
    status: 'warning',
    dueDate: 'Jun 2024',
    image: 'https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 5,
    product: 'Coconut Water - Original',
    issue: 'Nutrition facts panel requires reformatting',
    authority: 'FDA',
    status: 'low',
    dueDate: 'Dec 2024',
    image: 'https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=60&h=60&fit=crop&crop=center'
  }, {
    id: 6,
    product: 'Green Tea Extract - Capsules',
    issue: 'Health claims require additional substantiation',
    authority: 'FTC',
    status: 'low',
    dueDate: 'Aug 2024',
    image: 'https://images.unsplash.com/photo-1559181567-c3190ca9959b?w=60&h=60&fit=crop&crop=center'
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-422" />;
      case 'warning':
        return <Clock className="w-4 h-4 text-orange-600" data-id="element-423" />;
      case 'low':
        return <CheckCircle className="w-4 h-4 text-blue-600" data-id="element-424" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-orange-100 text-orange-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const filteredIssues = complianceIssues.filter(issue => {
    const matchesSearch = searchQuery === '' || issue.product.toLowerCase().includes(searchQuery.toLowerCase()) || issue.issue.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || issue.status === filterStatus;
    const matchesAuthority = filterAuthority === 'all' || issue.authority === filterAuthority;
    return matchesSearch && matchesStatus && matchesAuthority;
  });
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-425">
      <Sidebar data-id="element-426" />
      <div className="pl-64 transition-all duration-300 ease-in-out" data-id="element-427">
        <Header title="Compliance Monitoring" subtitle="Monitor regulatory changes and compliance issues affecting your products" data-id="element-428" />
        <main className="p-8" data-id="element-429">
          <div className="grid grid-cols-4 gap-6 mb-8" data-id="element-430">
            <div className="bg-white p-6 rounded-lg border flex flex-col" data-id="element-431">
              <div className="flex items-center justify-between mb-4" data-id="element-432">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-433">
                  Total Issues
                </h3>
                <div className="bg-red-100 p-2 rounded-lg" data-id="element-434">
                  <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-435" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-gray-900 mb-1" data-id="element-436">43</p>
              <div className="flex items-center text-sm text-red-600" data-id="element-437">
                <span data-id="element-438">+7 since last month</span>
              </div>
              <div className="mt-auto pt-4" data-id="element-439">
                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center" data-id="element-440">
                  <span data-id="element-441">View all issues</span>
                  <ArrowRight className="w-4 h-4 ml-1" data-id="element-442" />
                </button>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border flex flex-col" data-id="element-443">
              <div className="flex items-center justify-between mb-4" data-id="element-444">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-445">
                  Critical Issues
                </h3>
                <div className="bg-orange-100 p-2 rounded-lg" data-id="element-446">
                  <Bell className="w-5 h-5 text-orange-600" data-id="element-447" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-gray-900 mb-1" data-id="element-448">12</p>
              <div className="flex items-center text-sm text-orange-600" data-id="element-449">
                <span data-id="element-450">Requires immediate attention</span>
              </div>
              <div className="mt-auto pt-4" data-id="element-451">
                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center" data-id="element-452">
                  <span data-id="element-453">View critical issues</span>
                  <ArrowRight className="w-4 h-4 ml-1" data-id="element-454" />
                </button>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border flex flex-col" data-id="element-455">
              <div className="flex items-center justify-between mb-4" data-id="element-456">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-457">
                  Upcoming Deadlines
                </h3>
                <div className="bg-blue-100 p-2 rounded-lg" data-id="element-458">
                  <Calendar className="w-5 h-5 text-blue-600" data-id="element-459" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-gray-900 mb-1" data-id="element-460">7</p>
              <div className="flex items-center text-sm text-blue-600" data-id="element-461">
                <span data-id="element-462">Due in the next 30 days</span>
              </div>
              <div className="mt-auto pt-4" data-id="element-463">
                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center" data-id="element-464">
                  <span data-id="element-465">View calendar</span>
                  <ArrowRight className="w-4 h-4 ml-1" data-id="element-466" />
                </button>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border flex flex-col" data-id="element-467">
              <div className="flex items-center justify-between mb-4" data-id="element-468">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-469">
                  Compliance Score
                </h3>
                <div className="bg-green-100 p-2 rounded-lg" data-id="element-470">
                  <PieChart className="w-5 h-5 text-green-600" data-id="element-471" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-gray-900 mb-1" data-id="element-472">86%</p>
              <div className="flex items-center text-sm text-green-600" data-id="element-473">
                <span data-id="element-474">+2% from previous month</span>
              </div>
              <div className="mt-auto pt-4" data-id="element-475">
                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center" data-id="element-476">
                  <span data-id="element-477">View full report</span>
                  <ArrowRight className="w-4 h-4 ml-1" data-id="element-478" />
                </button>
              </div>
            </div>
          </div>
          <div className="flex space-x-4 mb-6" data-id="element-479">
            <button onClick={() => setActiveTab('issues')} className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center space-x-2 ${activeTab === 'issues' ? 'bg-purple-100 text-purple-700 border border-purple-200' : 'bg-white text-gray-700 border hover:bg-gray-50'}`} data-id="element-480">
              <AlertTriangle className="w-4 h-4" data-id="element-481" />
              <span data-id="element-482">Compliance Issues</span>
            </button>
            <button onClick={() => setActiveTab('news')} className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center space-x-2 ${activeTab === 'news' ? 'bg-purple-100 text-purple-700 border border-purple-200' : 'bg-white text-gray-700 border hover:bg-gray-50'}`} data-id="element-483">
              <Bell className="w-4 h-4" data-id="element-484" />
              <span data-id="element-485">News & Updates</span>
            </button>
            <button onClick={() => setActiveTab('assistant')} className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center space-x-2 ${activeTab === 'assistant' ? 'bg-purple-100 text-purple-700 border border-purple-200' : 'bg-white text-gray-700 border hover:bg-gray-50'}`} data-id="element-486">
              <BarChart3 className="w-4 h-4" data-id="element-487" />
              <span data-id="element-488">Compliance Assistant</span>
            </button>
          </div>
          {activeTab === 'assistant' && <ComplianceAssistant data-id="element-489" />}
          {activeTab === 'news' && <ComplianceNews data-id="element-490" />}
          {activeTab === 'issues' && <>
              <div className="bg-white rounded-lg border p-4 mb-6" data-id="element-491">
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-492">
                  <div className="flex-1 relative" data-id="element-493">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-494" />
                    <input type="text" placeholder="Search compliance issues..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-495" />
                  </div>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-496">
                    <option value="all" data-id="element-497">All Severities</option>
                    <option value="critical" data-id="element-498">Critical Issues</option>
                    <option value="warning" data-id="element-499">Warnings</option>
                    <option value="low" data-id="element-500">Low Priority</option>
                  </select>
                  <select value={filterAuthority} onChange={e => setFilterAuthority(e.target.value)} className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-501">
                    <option value="all" data-id="element-502">All Authorities</option>
                    <option value="FDA" data-id="element-503">FDA</option>
                    <option value="USDA" data-id="element-504">USDA</option>
                    <option value="FTC" data-id="element-505">FTC</option>
                  </select>
                </div>
              </div>
              <div className="bg-white rounded-lg border mb-8" data-id="element-506">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center" data-id="element-507">
                  <div data-id="element-508">
                    <h2 className="text-lg font-semibold text-gray-900" data-id="element-509">
                      Compliance Issues
                    </h2>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-510">
                      Products affected by regulatory changes
                    </p>
                  </div>
                  <div className="flex space-x-3" data-id="element-511">
                    <button className="flex items-center space-x-2 text-purple-600 hover:text-purple-700 text-sm font-medium" data-id="element-512">
                      <FileText className="w-4 h-4" data-id="element-513" />
                      <span data-id="element-514">Export Report</span>
                    </button>
                    <button className="flex items-center space-x-2 text-purple-600 hover:text-purple-700 text-sm font-medium" data-id="element-515">
                      <Download className="w-4 h-4" data-id="element-516" />
                      <span data-id="element-517">Download All</span>
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto" data-id="element-518">
                  <table className="w-full" data-id="element-519">
                    <thead className="bg-gray-50" data-id="element-520">
                      <tr data-id="element-521">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-522">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-523">
                          Compliance Issue
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-524">
                          Regulatory Authority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-525">
                          Severity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-526">
                          Due Date
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-527">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-528">
                      {filteredIssues.map(issue => <Fragment key={issue.id} data-id="element-529">
                          <tr className={`hover:bg-gray-50 ${expandedIssue === issue.id ? 'bg-gray-50' : ''}`} data-id="element-530">
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-531">
                              <div className="flex items-center" data-id="element-532">
                                <img src={issue.image} alt={issue.product} className="w-10 h-10 rounded-lg object-cover" data-id="element-533" />
                                <div className="ml-4" data-id="element-534">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-535">
                                    {issue.product}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4" data-id="element-536">
                              <div className="text-sm text-gray-900 max-w-md" data-id="element-537">
                                {issue.issue}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-538">
                              <div className="text-sm text-gray-900" data-id="element-539">
                                {issue.authority}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-540">
                              <div className="flex items-center space-x-2" data-id="element-541">
                                {getStatusIcon(issue.status)}
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(issue.status)}`} data-id="element-542">
                                  {issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-543">
                              {issue.dueDate}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-544">
                              <div className="flex items-center justify-end space-x-3" data-id="element-545">
                                <button className="text-purple-600 hover:text-purple-700 flex items-center space-x-1" onClick={() => setExpandedIssue(expandedIssue === issue.id ? null : issue.id)} data-id="element-546">
                                  <Eye className="w-4 h-4" data-id="element-547" />
                                  <span data-id="element-548">Details</span>
                                  <ChevronDown className={`w-4 h-4 transition-transform ${expandedIssue === issue.id ? 'rotate-180' : ''}`} data-id="element-549" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedIssue === issue.id && <tr data-id="element-550">
                              <td colSpan={6} className="px-6 py-4 bg-gray-50 border-t border-gray-100" data-id="element-551">
                                <div className="p-4 bg-white rounded-lg border border-gray-200" data-id="element-552">
                                  <h4 className="font-medium text-gray-900 mb-3" data-id="element-553">
                                    Issue Details & Actions
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-554">
                                    <div data-id="element-555">
                                      <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-556">
                                        Issue Description
                                      </h5>
                                      <p className="text-sm text-gray-600 mb-3" data-id="element-557">
                                        {issue.product} contains{' '}
                                        {issue.issue.includes('Red Food Coloring') ? 'Red Food Coloring #3, which is being phased out by the FDA due to potential health concerns' : issue.issue}
                                      </p>
                                      <div className="flex items-center space-x-2 mb-2" data-id="element-558">
                                        <span className="text-sm font-medium text-gray-700" data-id="element-559">
                                          Regulatory Authority:
                                        </span>
                                        <span className="text-sm text-gray-600" data-id="element-560">
                                          {issue.authority}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2 mb-2" data-id="element-561">
                                        <span className="text-sm font-medium text-gray-700" data-id="element-562">
                                          Compliance Deadline:
                                        </span>
                                        <span className="text-sm text-gray-600" data-id="element-563">
                                          {issue.dueDate}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2" data-id="element-564">
                                        <span className="text-sm font-medium text-gray-700" data-id="element-565">
                                          Severity:
                                        </span>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(issue.status)}`} data-id="element-566">
                                          {issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
                                        </span>
                                      </div>
                                    </div>
                                    <div data-id="element-567">
                                      <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-568">
                                        Recommended Actions
                                      </h5>
                                      <div className="space-y-3" data-id="element-569">
                                        <button className="w-full flex items-center justify-between p-3 text-left border rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors" data-id="element-570">
                                          <div className="flex items-center space-x-3" data-id="element-571">
                                            <div className="bg-purple-100 p-2 rounded-lg" data-id="element-572">
                                              <Beaker className="w-4 h-4 text-purple-600" data-id="element-573" />
                                            </div>
                                            <div data-id="element-574">
                                              <p className="text-sm font-medium text-gray-900" data-id="element-575">
                                                Escalate to R&D
                                              </p>
                                              <p className="text-xs text-gray-600" data-id="element-576">
                                                Request new BOM update with
                                                alternative ingredients
                                              </p>
                                            </div>
                                          </div>
                                          <ArrowUpRight className="w-4 h-4 text-purple-600" data-id="element-577" />
                                        </button>
                                        <button className="w-full flex items-center justify-between p-3 text-left border rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors" data-id="element-578">
                                          <div className="flex items-center space-x-3" data-id="element-579">
                                            <div className="bg-blue-100 p-2 rounded-lg" data-id="element-580">
                                              <ClipboardList className="w-4 h-4 text-blue-600" data-id="element-581" />
                                            </div>
                                            <div data-id="element-582">
                                              <p className="text-sm font-medium text-gray-900" data-id="element-583">
                                                Request Regulatory Review
                                              </p>
                                              <p className="text-xs text-gray-600" data-id="element-584">
                                                Get expert analysis of
                                                compliance requirements
                                              </p>
                                            </div>
                                          </div>
                                          <ArrowUpRight className="w-4 h-4 text-blue-600" data-id="element-585" />
                                        </button>
                                        <button className="w-full flex items-center justify-between p-3 text-left border rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors" data-id="element-586">
                                          <div className="flex items-center space-x-3" data-id="element-587">
                                            <div className="bg-green-100 p-2 rounded-lg" data-id="element-588">
                                              <MessageSquare className="w-4 h-4 text-green-600" data-id="element-589" />
                                            </div>
                                            <div data-id="element-590">
                                              <p className="text-sm font-medium text-gray-900" data-id="element-591">
                                                Notify Stakeholders
                                              </p>
                                              <p className="text-xs text-gray-600" data-id="element-592">
                                                Alert product and marketing
                                                teams
                                              </p>
                                            </div>
                                          </div>
                                          <ArrowUpRight className="w-4 h-4 text-green-600" data-id="element-593" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex justify-end space-x-3 pt-2 border-t" data-id="element-594">
                                    <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1" data-id="element-595">
                                      <FileText className="w-4 h-4" data-id="element-596" />
                                      <span data-id="element-597">View Full Report</span>
                                    </button>
                                    <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1" data-id="element-598">
                                      <MoreVertical className="w-4 h-4" data-id="element-599" />
                                      <span data-id="element-600">More Options</span>
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>}
                        </Fragment>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </>}
        </main>
      </div>
    </div>;
}